import { clstx } from '@payaca/utilities/cvaMerge';
import React, {
  FunctionComponent,
  PropsWithChildren,
  useContext,
  useMemo,
} from 'react';
import { Box, PolymorphicComponentProps } from 'react-polymorphic-box';

type CardSizeVariantLiteral = 'xs' | 'sm' | 'md' | 'lg';

const SizeContext = React.createContext<CardSizeVariantLiteral>('md');

export interface CardBaseProps {
  sizeVariant?: CardSizeVariantLiteral;
  variant?: 'default' | 'error';
}

export type CardProps<E extends React.ElementType> = PolymorphicComponentProps<
  E,
  CardBaseProps
>;

const defaultElement = 'div';

/**
 * A bordered container that groups related content.
 *
 * ## When to use
 *
 * - Grouping related content into a visually distinct block on a page
 * - Entity summaries or previews, with a title and supporting detail
 *
 * ## When to choose another component
 *
 * - **A clickable card that triggers an action** — use `CardButton`. It renders
 *   a real `button` with hover, disabled, and focus states.
 * - **A card that navigates** — use `CardAnchor`. It renders a real link,
 *   preserving browser behaviours.
 * - **Messages or callouts** — use `Alert`.
 *
 * ## Composition rules
 *
 * Put content in `Card.Body` — it applies the padding for the Card's
 * `sizeVariant`. Use `H1`–`H4` and `Text` for headings and text:
 *
 * ```tsx
 * <Card sizeVariant="md">
 *   <Card.Body>
 *     <H4>Title</H4>
 *     <Text>Body text</Text>
 *   </Card.Body>
 * </Card>
 * ```
 *
 * Do not make a plain Card interactive. Use the interactive wrappers instead:
 *
 * ✅ An action:
 *
 * ```tsx
 * <CardButton onClick={handleSelect}>
 *   <Card.Body>
 *     <H4>Title</H4>
 *   </Card.Body>
 * </CardButton>
 * ```
 *
 * ❌ `onClick` on a plain Card:
 *
 * ```tsx
 * <Card onClick={handleSelect}>
 *   <Card.Body>
 *     <H4>Title</H4>
 *   </Card.Body>
 * </Card>
 * ```
 *
 * Card is polymorphic via the `as` prop. Use it for semantic grouping elements
 * (e.g. `as="section"`, `as="li"`), not for interactivity.
 *
 * Do not nest a Card inside another Card.
 * Structure content inside a Card with plain markup (headings,
 * `Divider`, spacing) instead.
 *
 * A Card inside a `Modal` or `Sidebar` is fine.
 */
const Card: <E extends React.ElementType = typeof defaultElement>(
  props: CardProps<E>
) => React.ReactNode | null = React.forwardRef(
  <E extends React.ElementType = typeof defaultElement>(
    {
      children,
      sizeVariant = 'md',
      variant = 'default',
      className,
      ...rest
    }: CardProps<E>,
    ref: typeof rest.ref
  ) => {
    return (
      <SizeContext.Provider value={sizeVariant}>
        {/*It seems `react-polymorphic-box` is deprecated. And isn't compatible with typescript v5*/}
        {/*https://github.com/kripod/react-polymorphic-box/issues/26#issuecomment-2014522158*/}
        {/*@ts-expect-error*/}
        <Box
          as={defaultElement}
          className={clstx(
            'typography:prose flex flex-col rounded-xl border border-gray-200 bg-white shadow-sm',
            variant === 'error' && 'border-red-500',
            className
          )}
          ref={ref}
          {...rest}
        >
          {children}
        </Box>
      </SizeContext.Provider>
    );
  }
);

const Body: FunctionComponent<PropsWithChildren<{ className?: string }>> = ({
  children,
  className,
}) => {
  const sizeVariant = useContext(SizeContext);

  const sizeClasses = useMemo(() => {
    switch (sizeVariant) {
      case 'xs':
        return 'p-2 md:p-2';
      case 'lg':
        return 'md:p-7';
      case 'md':
        return 'md:p-5';
    }
  }, [sizeVariant]);

  return <div className={clstx('p-4', sizeClasses, className)}>{children}</div>;
};

export default Object.assign(Card, {
  Body,
});
